import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, SHIPPING_NOTICE } from '../data/mock';
import { useCart } from '../context/CartContext';
import { Truck, Shield, Plus, Minus, ShoppingBag, CheckCircle, ChevronRight, Home, Package } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  const product = products.find(p => p.slug === slug);
  const relatedProducts = products.filter(p => p.id !== product?.id).slice(0, 4);

  usePageTitle(product?.name || 'Product Not Found', product?.description);

  if (!product) {
    return (
      <div className="min-h-screen bg-warmWhite-100">
        <Header />
        <main className="pt-[130px] lg:pt-[140px] pb-20">
          <div className="max-w-lg mx-auto px-4 text-center">
            <div className="w-20 h-20 bg-cream-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-10 h-10 text-warmBrown-400" />
            </div>
            <h1 className="font-display text-2xl text-warmBrown-900 mb-4">Product Not Found</h1>
            <p className="text-warmBrown-500 mb-8">The product you're looking for doesn't exist.</p>
            <Link to="/shop">
              <button className="px-8 py-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-2xl transition-colors shadow-lg shadow-rose-600/20">
                Browse Products
              </button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[110px] lg:pt-[126px] pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-warmBrown-500 mb-6 py-4">
            <Link to="/" className="hover:text-rose-600 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Home
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/shop" className="hover:text-rose-600 transition-colors">Products</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-warmBrown-800 font-medium truncate max-w-[200px]">{product.name}</span>
          </nav>

          {/* Product Detail */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-20">
            {/* Image */}
            <div className="relative">
              <div className="aspect-square bg-white rounded-3xl overflow-hidden shadow-card border border-cream-200">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {product.badge && (
                <span className={`absolute top-4 left-4 lg:top-6 lg:left-6 px-3 lg:px-4 py-1.5 lg:py-2 text-white text-xs lg:text-sm font-semibold rounded-full ${
                  product.badge === 'Premium' || product.badge === 'Professional'
                    ? 'bg-warmBrown-700'
                    : product.badge === 'Popular'
                    ? 'bg-rose-500'
                    : 'bg-sage-500'
                }`}>
                  {product.badge}
                </span>
              )}
            </div>

            {/* Info */}
            <div className="flex flex-col">
              <div className="mb-6">
                <p className="text-rose-500 font-medium text-sm mb-2">SKU: {product.sku}</p>
                <h1 className="font-display text-2xl md:text-3xl lg:text-4xl text-warmBrown-900 mb-4">
                  {product.name}
                </h1>
                <p className="text-3xl lg:text-4xl font-display font-bold text-rose-600">
                  ${product.price.toFixed(2)}
                </p>
              </div>

              <p className="text-warmBrown-600 leading-relaxed mb-8">
                {product.description}
              </p>

              {/* Features */}
              <div className="mb-8">
                <h3 className="font-semibold text-warmBrown-900 mb-4 text-sm uppercase tracking-wider">Features</h3>
                <div className="grid grid-cols-2 gap-3">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-cream-100 rounded-2xl">
                      <CheckCircle className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-warmBrown-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex items-center bg-white border border-cream-200 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-4 hover:bg-cream-50 transition-colors"
                  >
                    <Minus className="w-5 h-5 text-warmBrown-600" />
                  </button>
                  <span className="w-14 text-center font-semibold text-warmBrown-900">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-4 hover:bg-cream-50 transition-colors"
                  >
                    <Plus className="w-5 h-5 text-warmBrown-600" />
                  </button>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={addedToCart}
                  className={`flex-1 py-4 px-8 font-semibold rounded-2xl transition-all flex items-center justify-center gap-2 ${
                    addedToCart
                      ? 'bg-sage-500 text-white'
                      : 'bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-600/20'
                  }`}
                >
                  {addedToCart ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Added to Cart!
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-5 h-5" />
                      Add to Cart
                    </>
                  )}
                </button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-2 gap-4 p-5 lg:p-6 bg-rose-50 rounded-2xl border border-rose-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                    <Truck className="w-5 h-5 text-rose-500" />
                  </div>
                  <div>
                    <p className="font-medium text-warmBrown-800 text-sm">Free Shipping</p>
                    <p className="text-warmBrown-500 text-xs">On orders $99+</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-5 h-5 text-rose-500" />
                  </div>
                  <div>
                    <p className="font-medium text-warmBrown-800 text-sm">30-Day Returns</p>
                    <p className="text-warmBrown-500 text-xs">Easy refunds</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Products */}
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-display text-xl lg:text-2xl text-warmBrown-900">
                You May Also Like
              </h2>
              <Link
                to="/shop"
                className="text-rose-600 hover:text-rose-700 font-medium text-sm transition-colors"
              >
                View All
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.slug}`}
                  className="group bg-white rounded-3xl overflow-hidden border border-cream-200 hover:border-rose-200 hover:shadow-card transition-all duration-300"
                >
                  <div className="aspect-square bg-cream-50 relative overflow-hidden">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3 lg:p-4">
                    <h3 className="font-medium text-warmBrown-800 mb-1 group-hover:text-rose-600 transition-colors line-clamp-1 text-sm">
                      {relatedProduct.name}
                    </h3>
                    <p className="text-lg font-display font-bold text-rose-600">${relatedProduct.price.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
